// src/pages/Servicios/ServicioDetalle.jsx
import { Box, Typography, Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, useMediaQuery, Chip, Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import { fetchServiceById, updateServiceComment, toggleCancelService } from "../../../services/serviceService";
import { useTheme, alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* ============== Leaflet icons fix ============== */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

/* ============== Utils ============== */
const dmsToDecimal = (coord) => {
  if (!coord) return NaN;
  const match = coord.match(/(\d+)°([\d.]+)'([NSEW])/);
  if (!match) return NaN;
  const [, degrees, minutes, direction] = match;
  let decimal = parseInt(degrees) + parseFloat(minutes) / 60;
  if (direction === "S" || direction === "W") decimal *= -1;
  return decimal;
};

const parseCoords = (coordStr) => {
  if (typeof coordStr !== "string") return null;
  const [latStr, lngStr] = coordStr.split(" ");
  const lat = dmsToDecimal(latStr);
  const lng = dmsToDecimal(lngStr);
  return !isNaN(lat) && !isNaN(lng) ? [lat, lng] : null;
};

const pointToLatLng = (point) =>
  point && Array.isArray(point.coordinates) && point.coordinates.length === 2
    ? [point.coordinates[1], point.coordinates[0]]
    : null;

const mapServiceForUi = (svc) => {
  if (!svc) return null;
  return {
    ...svc,
    SERVICIO: svc.externalId || svc._id,
    "FECHA INICIO": svc.fechaInicio ? new Date(svc.fechaInicio).toLocaleString() : "",
    "FECHA FIN": svc.fechaFin ? new Date(svc.fechaFin).toLocaleString() : "",
    "MÉTODO DE PAGO": svc.metodoPago,
    "TIPO DE SERVICIO": svc.tipoServicio,
    "DISTANCIA km.": svc.distanciaKm ?? "",
    "DIRECCIÓN INICIO": svc.direccionInicio,
    "DIRECCIÓN FIN": svc.direccionFin,
    TARIFA: svc.tarifa,
    "TIPO CONCERTADO": svc.tipoConcertado,
    "IMPORTE TAXÍMETRO EUR": svc.importes?.taximetro ?? svc.taximetro ?? 0,
    "SUPLEMENTOS EUR": svc.importes?.suplementos ?? 0,
    "PROPINAS EUR": svc.importes?.propinas ?? 0,
    "PEAJES EUR": svc.importes?.peajes ?? 0,
    "TOTAL EUR": svc.importes?.total ?? svc.importeTotal ?? 0,
    COMENTARIO: svc.comentario ?? svc.notas ?? "",
    "POSICIÓN INICIO": svc.posicionInicio
      ? `${svc.posicionInicio.coordinates[1]} ${svc.posicionInicio.coordinates[0]}`
      : svc["POSICIÓN INICIO"],
    "POSICIÓN FIN": svc.posicionFin
      ? `${svc.posicionFin.coordinates[1]} ${svc.posicionFin.coordinates[0]}`
      : svc["POSICIÓN FIN"],
    cancelado: Boolean(svc.cancelado),
  };
};

const FitToMap = ({ positions }) => {
  const map = useMap();
  if (!map) return null;
  if (positions.length === 2) {
    map.fitBounds(positions);
  } else if (positions.length === 1) {
    map.setView(positions[0], 15);
  }
  return null;
};

/* ============== Paletas (idénticas al otro tab) ============== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};

const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/* ============== Patrones reutilizables (compactados) ============== */
function DataChip({ label, value, palette, highlight = false, compact = false }) {
  return (
    <Box
      sx={{
        p: compact ? 1 : 1.5,                      // antes 1.25 / 2
        borderRadius: "10px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        minHeight: compact ? 44 : 60,              // antes 56 / 76
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 5px 14px rgba(2, 6, 23, 0.08)"
            : "0 2px 6px rgba(0,0,0,0.18)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: palette.label,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          mb: compact ? 0.25 : 0.4,
          fontWeight: 600,
          fontSize: 11.5,                          // más pequeño
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: palette.text,
          fontWeight: 700,
          fontSize: compact ? 14 : 18,             // antes 16 / 20
          lineHeight: 1.15,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function InfoCard({ title, children, palette }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "10px",
        p: 1.5,                                     // antes 2
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 5px 14px rgba(2, 6, 23, 0.08)"
            : "0 2px 6px rgba(0,0,0,0.18)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: palette.label,
          textTransform: "uppercase",
          letterSpacing: 0.45,
          mb: 1,                                    // antes 1.5
          fontWeight: 700,
          fontSize: 12,                             // un pelín más pequeño
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function RowItem({ label, value, palette, highlight = false }) {
  return (
    <Box
      sx={{
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        borderRadius: "10px",
        p: 1,                                       // antes 1.5
      }}
    >
      <Typography
        sx={{
          color: palette.label,
          mb: 0.4,
          fontSize: 11.5,                           // más pequeño
          textTransform: "none",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: palette.text,
          fontWeight: 600,
          fontSize: 15,                              // más pequeño
          lineHeight: 1.2,
        }}
      >
        {value ?? "No disponible"}
      </Typography>
    </Box>
  );
}

/* ============== Página ============== */
const ServicioDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = isDark ? PALETTE_DARK : PALETTE_LIGHT;
  const isMobile = useMediaQuery("(max-width:600px)");
  const [servicio, setServicio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comentario, setComentario] = useState("");
  const [commentDraft, setCommentDraft] = useState("");
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [togglingCancel, setTogglingCancel] = useState(false);
  const [toggleError, setToggleError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchServiceById(id);
        const mapped = mapServiceForUi(data);
        setServicio(mapped);
        const initialComment = data?.comentario ?? data?.notas ?? "";
        setComentario(initialComment);
        setCommentDraft(initialComment);
      } catch (e) {
        setError(e.message || "No se pudo cargar el servicio");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const total = useMemo(() => {
    if (!servicio) return 0;
    return Number(servicio["TOTAL EUR"] ?? servicio.importes?.total ?? servicio.importeTotal ?? 0);
  }, [servicio]);
  const taximetro = useMemo(() => {
    if (!servicio) return 0;
    return Number(servicio["IMPORTE TAXÍMETRO EUR"] ?? servicio.importes?.taximetro ?? servicio.taximetro ?? 0);
  }, [servicio]);
  const distancia = servicio?.["DISTANCIA km."] ?? servicio?.distanciaKm ?? 0;
  const metodoPago = servicio?.["MÉTODO DE PAGO"] ?? servicio?.metodoPago ?? "-";

  const posInicio =
    pointToLatLng(servicio?.posicionInicio) ||
    parseCoords(servicio?.["POSICIÓN INICIO"]);
  const posFin =
    pointToLatLng(servicio?.posicionFin) ||
    parseCoords(servicio?.["POSICIÓN FIN"]);
  const positions = [posInicio, posFin].filter(Boolean);

  const handleOpenComment = () => {
    setCommentError("");
    setCommentDraft(comentario);
    setIsCommentDialogOpen(true);
  };

  const handleToggleCancel = async () => {
    if (!servicio?.SERVICIO) return;
    try {
      setTogglingCancel(true);
      setToggleError("");
      const nextState = !servicio.cancelado;
      const updated = await toggleCancelService(servicio.SERVICIO, nextState);
      const nextCancelled = Boolean(updated?.service?.cancelado ?? nextState);
      setServicio((prev) =>
        prev
          ? { ...prev, cancelado: nextCancelled }
          : prev
      );
    } catch (e) {
      setToggleError(e.message || "No se pudo actualizar el servicio");
    } finally {
      setTogglingCancel(false);
    }
  };

  const handleSaveComment = async () => {
    try {
      setSavingComment(true);
      setCommentError("");
      const updated = await updateServiceComment(id, commentDraft);
      const nextComment = updated?.comentario ?? commentDraft;
      setComentario(nextComment);
      setServicio((prev) =>
        prev ? { ...prev, COMENTARIO: nextComment, comentario: nextComment } : prev
      );
      setIsCommentDialogOpen(false);
    } catch (e) {
      setCommentError(e.message || "No se pudo guardar el comentario");
    } finally {
      setSavingComment(false);
    }
  };

  const velocidadMax = Number(servicio?.velocidadMaxima ?? 0);
  const velocidadMaxDesdeUlt = Number(
    servicio?.velocidadMaximaDesdeUltimo ?? 0
  );
  const highlightVelocidad = velocidadMax > 60 && total < 6;

  if (loading) return <Typography sx={{ m: 2 }}>Cargando servicio...</Typography>;
  if (error) return <Typography color="error" sx={{ m: 2 }}>{error}</Typography>;
  if (!servicio) return <Typography>No se encontró el servicio</Typography>;

  return (
    <Box
      sx={{
        mr: "20px",
        ml: "20px",
        pb: "20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",          // esta página ocupa toda la altura disponible del layout
        minHeight: 0,
        boxSizing: "border-box",
      }}
    >
      {/* Volver */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          color: palette.title,
          textTransform: "none",
          fontSize: "14px",                         // un poco más pequeño
          fontWeight: 500,
          alignSelf: "flex-start",
          "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
        }}
      >
        Volver
      </Button>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 0.75, sm: 1 },
          flexWrap: "wrap",
          mt: 0.5,
          mb: 0.75,
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 0, sm: 1 } }}>
          <Header title={`Servicio #${id}`} subtitle="Detalle del servicio" />
        </Box>
        {!isMobile && (
          <Button
            variant="contained"
            onClick={handleOpenComment}
            sx={{
              backgroundColor: palette.btn,
              color: "white",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: palette.btnHover },
            }}
          >
            {comentario?.trim() ? "Editar comentario" : "Añadir comentario"}
          </Button>
        )}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {servicio?.cancelado && (
          <Chip
            label="Servicio anulado"
            color="error"
            size="small"
            sx={{ alignSelf: "flex-start" }}
          />
        )}
        <Button
          variant={servicio?.cancelado ? "outlined" : "contained"}
          color={servicio?.cancelado ? "primary" : "error"}
          onClick={handleToggleCancel}
          disabled={togglingCancel}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderColor: servicio?.cancelado ? palette.accent : undefined,
            color: palette.text,
            backgroundColor: servicio?.cancelado ? "transparent" : alpha("#ef4444", 0.14),
            "&:hover": {
              backgroundColor: servicio?.cancelado ? alpha(palette.accent, 0.12) : alpha("#ef4444", 0.22),
            },
          }}
        >
          {togglingCancel
            ? "Guardando..."
            : servicio?.cancelado
            ? "Reactivar servicio"
            : "Anular servicio"}
        </Button>
      </Stack>
      {commentError && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {commentError}
        </Typography>
      )}
      {toggleError && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {toggleError}
        </Typography>
      )}

      {/* ====== KPIs superiores (4 columnas iguales) ====== */}
      <Grid container spacing={1.5} sx={{ mt: 1, mb: 2.5 }}>  {/* spacing más compacto */}
        <Grid item xs={12} sm={6} md={3}>
          <DataChip label="Importe Total" value={`€${total.toFixed(2)}`} palette={palette} highlight />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataChip label="Taxímetro" value={`€${taximetro.toFixed(2)}`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataChip label="Distancia" value={`${distancia} km`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DataChip label="Método de Pago" value={metodoPago} palette={palette} />
        </Grid>
      </Grid>

      {/* ====== Dos columnas: izquierda con scroll, derecha se estira (mapa) ====== */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: { xs: "block", lg: "grid" },
          gridTemplateColumns: { lg: "1fr 50%" },
          columnGap: 3.5,                            // un pelín menor
          alignItems: "stretch",
        }}
      >
        {/* IZQUIERDA - scroll propio */}
        <Box
          sx={{
            height: { xs: "auto", lg: "100%" },
            minHeight: 0,
            overflowY: { xs: "visible", lg: "auto" },
            pr: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,                                 // antes 2
            pb: 1.5,                                  // antes 2
          }}
        >
          <InfoCard title="Tiempos" palette={palette}>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <RowItem label="Inicio" value={servicio["FECHA INICIO"]} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RowItem label="Fin" value={servicio["FECHA FIN"]} palette={palette} />
              </Grid>
            </Grid>
          </InfoCard>

          <InfoCard title="Pago y Tarifa" palette={palette}>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <RowItem label="Método de Pago" value={servicio["MÉTODO DE PAGO"]} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RowItem label="Tarifa" value={servicio["TARIFA"]} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RowItem label="Tipo de Servicio" value={servicio["TIPO DE SERVICIO"]} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RowItem label="Tipo Concertado" value={servicio["TIPO CONCERTADO"]} palette={palette} />
              </Grid>
            </Grid>
          </InfoCard>

          <InfoCard title="Trayecto" palette={palette}>
            <Grid container spacing={1.25}>
              <Grid item xs={12}>
                <RowItem label="Dirección Inicio" value={servicio["DIRECCIÓN INICIO"]} palette={palette} />
              </Grid>
              <Grid item xs={12}>
                <RowItem label="Dirección Fin" value={servicio["DIRECCIÓN FIN"]} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RowItem label="Distancia (km)" value={servicio["DISTANCIA km."]} palette={palette} />
              </Grid>
            </Grid>
          </InfoCard>

          <InfoCard title="Importes" palette={palette}>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={4}>
                <RowItem label="Taxímetro" value={`€${taximetro.toFixed(2)}`} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RowItem label="Suplementos" value={`€${servicio["SUPLEMENTOS EUR"]}`} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RowItem label="Propinas" value={`€${servicio["PROPINAS EUR"]}`} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RowItem label="Peajes" value={`€${servicio["PEAJES EUR"]}`} palette={palette} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <RowItem label="Total" value={`€${total.toFixed(2)}`} palette={palette} highlight />
              </Grid>
            </Grid>
          </InfoCard>

          <InfoCard title="Comentario" palette={palette}>
            <Typography
              sx={{
                color: palette.text,
                fontWeight: 600,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {comentario?.trim() ? comentario : "Sin comentario añadido."}
            </Typography>
          </InfoCard>
          {isMobile && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleOpenComment}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
              {comentario?.trim() ? "Editar comentario" : "Añadir comentario"}
            </Button>
          )}

          <InfoCard title="Velocidades" palette={palette}>
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <RowItem
                  label="Velocidad Máxima"
                  value={`${velocidadMax} km/h`}
                  palette={palette}
                  highlight={highlightVelocidad}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RowItem
                  label="Velocidad Máxima desde Último Servicio"
                  value={`${velocidadMaxDesdeUlt} km/h`}
                  palette={palette}
                />
              </Grid>
            </Grid>
          </InfoCard>

          <InfoCard title="Coordenadas" palette={palette}>
            <Grid container spacing={1.25}>
              <Grid item xs={12}>
                <RowItem label="Posición Inicio" value={servicio["POSICIÓN INICIO"]} palette={palette} />
              </Grid>
              <Grid item xs={12}>
                <RowItem label="Posición Fin" value={servicio["POSICIÓN FIN"]} palette={palette} />
              </Grid>
            </Grid>
          </InfoCard>
        </Box>

        {/* DERECHA - mapa: ocupa 100% del alto de la fila */}
        <Box
          sx={{
            height: { xs: 320, lg: "100%" },         // un poco más bajo en móvil (antes 360)
            mt: { xs: 2, lg: 0 },
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              backgroundColor: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: "10px",
              boxShadow:
                palette === PALETTE_LIGHT
                  ? "0 8px 20px rgba(2, 6, 23, 0.08)"
                  : "0 3px 10px rgba(0,0,0,0.22)",
              overflow: "hidden",
              height: "100%",
            }}
          >
            {positions.length > 0 ? (
              <MapContainer style={{ height: "100%", width: "100%" }}>
                <FitToMap positions={positions} />
                <TileLayer
                  attribution='&copy; <a href="https://osm.org">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {positions.map((pos, idx) => (
                  <Marker key={idx} position={pos}>
                    <Popup>{idx === 0 ? "Posición Inicio" : "Posición Fin"}</Popup>
                  </Marker>
                ))}
              </MapContainer>
            ) : (
              <Box sx={{ p: 1.5 }}>
                <Typography sx={{ color: palette.text, fontSize: 14 }}>
                  No hay coordenadas válidas para mostrar en el mapa.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={isCommentDialogOpen}
        onClose={() => setIsCommentDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            color: palette.text,
            boxShadow:
              isDark
                ? "0 16px 38px rgba(0,0,0,0.55)"
                : "0 14px 30px rgba(15,23,42,0.16)",
          },
        }}
      >
        <DialogTitle sx={{ color: palette.title, fontWeight: 700, pb: 1 }}>
          Comentario del servicio
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: palette.cardBorder }}>
          <TextField
            label="Comentario"
            fullWidth
            multiline
            minRows={3}
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: isDark ? "#0b1628" : "#f4f6fb",
                "& fieldset": { borderColor: palette.cardBorder },
                "&:hover fieldset": { borderColor: palette.cardBorder },
                "&.Mui-focused fieldset": { borderColor: palette.accent, borderWidth: 2 },
              },
              "& .MuiInputLabel-root": {
                color: palette.label,
                "&.Mui-focused": { color: palette.title },
              },
              "& .MuiInputBase-input": { color: palette.text },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsCommentDialogOpen(false)}
            disabled={savingComment}
            sx={{ textTransform: "none", color: palette.text }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveComment}
            disabled={savingComment}
            sx={{
              textTransform: "none",
              backgroundColor: palette.btn,
              color: "white",
              fontWeight: 600,
              "&:hover": { backgroundColor: palette.btnHover },
            }}
          >
            {savingComment ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicioDetalle;
